import React, { useMemo } from 'react';
import { AppButton, Box, Preloader } from '@common-fe/common-fe';

import { FilterRecord, FilterRecordCallback, ReportTab, ReportTypeKeys } from '../../report.types';
import {
  CIPStatusReportDetails,
  ClaimsActivityReportDetails,
  ContributionInvoiceReportDetails,
  DelinquentClaimsReportDetails,
  EmployeeDemographicsReportDetails,
  ForfeitureReportDetails,
  ParticipantActivityReportDetails,
  PayrollContributionReportDetails,
  PlanOmnibusBalanceReportDetails,
  ReplenishmentInvoiceReportDetails,
  UndeliverableMailReportDetails,
} from '../../ReportDetails';
import { CardStatusReportDetails } from '../../ReportDetails/CardStatusReport';
import HealthPlanEnrollmentsReportDetails from '../../ReportDetails/HealthPlanEnrollmentsReport/HealthPlanEnrollmentReportDetails';
import { PrefundInvoiceReportDetails } from '../../ReportDetails/PrefundInvoiceReport';

import { useIsMultipleEmployerScheduledReport } from './hooks';

interface Props {
  type: ReportTypeKeys;
  tab?: ReportTab;
  defaultEmployerId?: string;
  defaultEmployerIds?: string[];
  isEditMode?: boolean;
  isLoading?: boolean;
  onChangeFilters: FilterRecordCallback;
  defaultFilterValues?: Partial<FilterRecord>;
  onBack: () => void;
  onNext: () => void;
}
const ScheduledReportPreview: React.FC<Props> = ({
  type,
  tab,
  onBack,
  isEditMode,
  defaultEmployerId,
  defaultEmployerIds,
  isLoading,
  onNext,
  onChangeFilters,
  defaultFilterValues = {},
}) => {
  const { isMultipleEmployersMode } = useIsMultipleEmployerScheduledReport(type);

  const node = useMemo(() => {
    if (type === ReportTypeKeys.CardStatus) {
      return (
        <CardStatusReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.DelinquentClaims) {
      return (
        <DelinquentClaimsReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.CIPStatus) {
      return (
        <CIPStatusReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.PlanOmnibusBalance) {
      return (
        <PlanOmnibusBalanceReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.ClaimsActivity) {
      return (
        <ClaimsActivityReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.ContributionInvoice) {
      return (
        <ContributionInvoiceReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.EmployeeDemographic) {
      return (
        <EmployeeDemographicsReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.Forfeiture) {
      return (
        <ForfeitureReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.HealthPlanEnrollment) {
      return (
        <HealthPlanEnrollmentsReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.ParticipantActivity) {
      return (
        <ParticipantActivityReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.Payroll) {
      return (
        <PayrollContributionReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.PrefundInvoiceCard) {
      return (
        <PrefundInvoiceReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.ReplenishmentInvoice) {
      return (
        <ReplenishmentInvoiceReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
    if (type === ReportTypeKeys.UndeliverableAddress) {
      return (
        <UndeliverableMailReportDetails
          isPreviewMode
          defaultEmployerId={defaultEmployerId}
          defaultEmployerIds={defaultEmployerIds}
          defaultFilterValues={defaultFilterValues}
          defaultTab={tab}
          onChangeFilters={onChangeFilters}
        />
      );
    }
  }, [defaultEmployerId, defaultFilterValues, onChangeFilters, tab, type, defaultEmployerIds]);

  return (
    <Box direction="column">
      <Box data-testId="scheduled-report_preview">{node}</Box>
      <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
        <AppButton testId="scheduled-report_back" buttonType="secondary" width="160px" onClick={onBack}>
          Back
        </AppButton>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            disabled={
              isLoading || (!defaultEmployerId && !defaultFilterValues.employerId && !isMultipleEmployersMode)
            }
            testId="scheduled-report_submit"
            onClick={onNext}
            width="160px"
          >
            {isLoading ? <Preloader /> : <>{isEditMode ? 'Update' : 'Submit'} (2/2)</>}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ScheduledReportPreview;
